import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faCarSide,
  faDollarSign,
  faEnvelope,
  faHandshake,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

const AnalyticsSummary = ({ compact = false }) => {
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/analytics/admin-summary`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          },
        );
        const text = await response.text();
        const result = text ? safeJsonParse(text) : {};
        if (!response.ok) {
          throw new Error(
            result?.message ||
              "Analytics are not available yet. Please redeploy the API or clear the route cache.",
          );
        }
        if (!result) {
          throw new Error(
            "Analytics returned an unexpected response. Please make sure the API endpoint is deployed.",
          );
        }
        setAnalytics(result);
      } catch (err) {
        setError(err.message || "Unable to load analytics.");
      } finally {
        setIsLoading(false);
      }
    };

    loadAnalytics();
  }, []);

  if (isLoading) {
    return (
      <div className="rounded-xl bg-white p-5 shadow-lg">
        Loading analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 p-5 text-red-700 shadow-sm">
        {error}
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const smsCostCurrency = summary.smsCostCurrency || "USD";

  return (
    <div className="rounded-xl bg-white p-5 shadow-lg sm:p-6">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-[#004a7e]">
            Analytics
          </p>
          <h2 className="text-xl font-black text-gray-900 sm:text-2xl">
            Activity Snapshot
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            A quick look at event volume, ride activity, SMS usage, active
            people, and frequent pairings.
          </p>
        </div>
        {compact && (
          <Link
            to="/analytics"
            className="inline-flex items-center justify-center rounded-lg bg-[#004a7e] px-3 py-2 text-sm font-bold text-white hover:bg-[#003a64]"
          >
            View analytics
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5 xl:grid-cols-10">
        <StatCard
          icon={faCalendarCheck}
          label="Total events"
          value={summary.totalEvents}
        />
        <StatCard
          icon={faCalendarCheck}
          label="Upcoming"
          value={summary.upcomingEvents}
        />
        <StatCard
          icon={faCalendarCheck}
          label="Past"
          value={summary.pastEvents}
        />
        <StatCard
          icon={faUsers}
          label="Unique participants"
          value={summary.totalParticipants}
        />
        <StatCard
          icon={faCarSide}
          label="Unique drivers"
          value={summary.totalDrivers}
        />
        <StatCard
          icon={faUsers}
          label="Ride requests"
          value={summary.rideRequests}
        />
        <StatCard
          icon={faHandshake}
          label="Assigned rides"
          value={summary.assignedRides}
        />
        <StatCard
          icon={faEnvelope}
          label="SMS messages"
          value={summary.smsMessages}
        />
        <StatCard
          icon={faEnvelope}
          label="SMS segments"
          value={summary.smsSegments}
        />
        <StatCard
          icon={faDollarSign}
          label="SMS cost"
          value={formatCurrency(summary.smsEstimatedCost, smsCostCurrency)}
        />
      </div>

      {!compact && (
        <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-4">
          <RankList
            title="Most Active Drivers"
            items={analytics.topDrivers}
            empty="No driver activity yet."
            renderMeta={(item) =>
              `${item.ridersAssigned} riders assigned · ${item.eventsDriven} events driven · ${item.capacityOffered} seats`
            }
          />
          <RankList
            title="Most Active Participants"
            items={analytics.topParticipants}
            empty="No participant activity yet."
            renderMeta={(item) =>
              `${item.eventsParticipated} events · ${item.rideRequests} ride requests · ${item.assignedRides} assigned`
            }
          />
          <PairList pairs={analytics.topPairs} />
          <SmsUsageList items={analytics.smsUsageByEvent} />
        </div>
      )}
    </div>
  );
};

const safeJsonParse = (text) => {
  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
};

const formatCurrency = (value, currency = "USD") =>
  Number(value || 0).toLocaleString(undefined, {
    style: "currency",
    currency,
  });

const StatCard = ({ icon, label, value }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#eaf3fa] text-[#004a7e]">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
          {label}
        </p>
        <p className="text-2xl font-black text-[#004a7e]">{value || 0}</p>
      </div>
    </div>
  </div>
);

const RankList = ({ title, items = [], empty, renderMeta }) => (
  <div className="rounded-lg border border-gray-100">
    <div className="border-b border-gray-100 bg-gray-50 px-4 py-3 font-black text-gray-900">
      {title}
    </div>
    <div className="divide-y divide-gray-100">
      {items.length === 0 ? (
        <p className="p-4 text-sm text-gray-500">{empty}</p>
      ) : (
        items.map((item, index) => (
          <div key={item.id} className="flex gap-3 p-4">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#eaf3fa] text-xs font-black text-[#004a7e]">
              {index + 1}
            </span>
            <div className="min-w-0">
              <p className="truncate font-bold text-gray-900">
                {item.name || "Unnamed"}
              </p>
              <p className="text-xs text-gray-500">{renderMeta(item)}</p>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

const PairList = ({ pairs = [] }) => (
  <div className="rounded-lg border border-gray-100">
    <div className="border-b border-gray-100 bg-gray-50 px-4 py-3 font-black text-gray-900">
      Frequent Driver-Participant Pairs
    </div>
    <div className="divide-y divide-gray-100">
      {pairs.length === 0 ? (
        <p className="p-4 text-sm text-gray-500">No repeated pairings yet.</p>
      ) : (
        pairs.map((pair, index) => (
          <div
            key={`${pair.driverId}-${pair.participantId}`}
            className="flex gap-3 p-4"
          >
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#eaf3fa] text-xs font-black text-[#004a7e]">
              {index + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-gray-900">
                {pair.driverName} → {pair.participantName}
              </p>
              <p className="text-xs text-gray-500">
                Paired {pair.count} time{pair.count === 1 ? "" : "s"}
              </p>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

const SmsUsageList = ({ items = [] }) => (
  <div className="rounded-lg border border-gray-100">
    <div className="border-b border-gray-100 bg-gray-50 px-4 py-3 font-black text-gray-900">
      SMS Usage & Cost by Event
    </div>
    <div className="divide-y divide-gray-100">
      {items.length === 0 ? (
        <p className="p-4 text-sm text-gray-500">
          No event SMS usage tracked yet.
        </p>
      ) : (
        items.map((item, index) => (
          <div key={item.eventId} className="flex gap-3 p-4">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#eaf3fa] text-xs font-black text-[#004a7e]">
              {index + 1}
            </span>
            <div className="min-w-0">
              <p className="truncate font-bold text-gray-900">
                {item.title || "Untitled event"}
              </p>
              <p className="text-xs text-gray-500">
                {item.messages || 0} messages · {item.segments || 0} SMS
                segments · {formatCurrency(item.estimatedCost, item.currency)}
              </p>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

export default AnalyticsSummary;
